import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { calculateInvoiceNights, computeRegistrationTotal } from '../../../utils/invoiceModel';

interface FinancialSummaryData {
  registrationFees: number;
  accommodationFees: {
    apartments: number;
    dormsFireside: number;
    cottages: number;
    privateCottages: number;
    campingSites: number;
    orphaned: number;
    total: number;
  };
  fridgeRentals: number;
  meals: number;
  creditCardFees: number;
  otherFees: number;
  donations: number;
  totalIncome: number;
  payments: number;
  amountStillOwing: number;
  amountOverPaid: number;
  total: number;
}

const roundMoney = (amount: number): number => Math.round(amount * 100) / 100;

const sumPayments = (registration: any): number => {
  const payments = registration?.paymentHistory || registration?.payments || [];
  if (!Array.isArray(payments)) return 0;
  return roundMoney(
    payments.reduce((sum: number, payment: any) => {
      if (typeof payment?.amount === 'number') return sum + payment.amount;
      return sum;
    }, 0)
  );
};

// Breaks a registration's live charges into report lines using the same rules as
// computeRegistrationTotal, so Income lines + Status always reconcile with AR.
const getLiveChargeBreakdown = (registration: any, settings: any) => {
  let registrationFees = 0;
  let accommodationFee = 0;
  let fridgeRentals = 0;
  let meals = 0;
  let donations = 0;
  let creditCardFees = 0;
  let otherFees = 0;
  let priceCode = '';

  if (registration?.financial?.accommodationFees?.assignedAccommodation && settings?.rates?.accommodation) {
    accommodationFee = Number(registration.financial.accommodationFees.accommodationFee) || 0;
    priceCode = registration.financial.accommodationFees.priceCode || '';
  }

  if (Array.isArray(registration?.attendingCampers) && settings?.rates?.registration) {
    registration.attendingCampers.forEach((camper: any) => {
      const nights =
        camper.nights ||
        calculateInvoiceNights(registration?.campDates?.arrivalDate, registration?.campDates?.departureDate);
      const isFullCamp = nights >= 5;

      let rate = 0;
      if (camper.age === 'Adult' && settings.rates.registration.adult) {
        rate = isFullCamp
          ? settings.rates.registration.adult.weekly || 0
          : (settings.rates.registration.adult.nightly || 0) * nights;
      } else if (camper.age === 'AdultStudent' && settings.rates.registration.adultStudent) {
        rate = isFullCamp
          ? settings.rates.registration.adultStudent.weekly || 0
          : (settings.rates.registration.adultStudent.nightly || 0) * nights;
      } else if (camper.age === 'YouthChildren' && settings.rates.registration.youthAndChildren) {
        rate = isFullCamp
          ? settings.rates.registration.youthAndChildren.weekly || 0
          : (settings.rates.registration.youthAndChildren.nightly || 0) * nights;
      }
      registrationFees += rate;
    });
  }

  if (settings?.rates?.meals) {
    const fullCampAdultRate = settings.rates.meals.fullCampAdult || 0;
    const fullCampChildRate = settings.rates.meals.fullCampChild || 0;
    const dailyAdultRate = settings.rates.meals.dailyAdult || 0;
    const dailyChildRate = settings.rates.meals.dailyChild || 0;

    const mealData = registration?.financial?.otherFees?.meals;
    if (mealData) {
      meals += (mealData.fullCampPassesAdult?.quantity || 0) * fullCampAdultRate;
      meals += (mealData.fullCampPassesChild?.quantity || 0) * fullCampChildRate;
      meals += (mealData.dailyPassesAdult?.quantity || 0) * dailyAdultRate;
      meals += (mealData.dailyPassesChild?.quantity || 0) * dailyChildRate;
    } else {
      const legacyMeals = registration?.financial?.mealFees || registration?.mealFees;
      if (legacyMeals) {
        const fullCampAdult = legacyMeals.fullCampPassesAdult || legacyMeals.fullCampPasses?.adult || 0;
        const fullCampChild = legacyMeals.fullCampPassesChild || legacyMeals.fullCampPasses?.child || 0;
        const dailyAdult = legacyMeals.dailyPassesAdult || legacyMeals.dailyPasses?.adult || 0;
        const dailyChild = legacyMeals.dailyPassesChild || legacyMeals.dailyPasses?.child || 0;
        meals += fullCampAdult * fullCampAdultRate;
        meals += fullCampChild * fullCampChildRate;
        meals += dailyAdult * dailyAdultRate;
        meals += dailyChild * dailyChildRate;
      }
    }
  }

  if (registration?.financial?.otherFees) {
    const otherFeesData = registration.financial.otherFees;
    fridgeRentals += Number(otherFeesData.fridge?.cost ?? otherFeesData.fridgeRental ?? 0) || 0;
    donations += Number(otherFeesData.donations?.amount ?? otherFeesData.donation ?? 0) || 0;
    otherFees += Number(otherFeesData.customFee?.amount) || 0;

    for (const fee of otherFeesData.otherFees || []) {
      const amount = Number(fee?.amount) || 0;
      const note = String(fee?.note || '');
      if (/^cc\s*fee$/i.test(note.trim()) || String(fee?.id || '').startsWith('ccfee_')) {
        creditCardFees += amount;
      } else {
        otherFees += amount;
      }
    }
  }

  const savedOverrides = registration?.financial?.overrides;
  if (savedOverrides?.accommodation?.enabled) {
    accommodationFee += Number(savedOverrides.accommodation.amount) || 0;
  }
  if (savedOverrides?.registration?.enabled) {
    registrationFees += Number(savedOverrides.registration.amount) || 0;
  }
  if (savedOverrides?.mealFees?.enabled) {
    meals += Number(savedOverrides.mealFees.amount) || 0;
  }
  if (savedOverrides?.otherFees?.enabled) {
    otherFees += Number(savedOverrides.otherFees.amount) || 0;
  }

  return {
    registrationFees: roundMoney(registrationFees),
    accommodationFee: roundMoney(accommodationFee),
    fridgeRentals: roundMoney(fridgeRentals),
    meals: roundMoney(meals),
    donations: roundMoney(donations),
    creditCardFees: roundMoney(creditCardFees),
    otherFees: roundMoney(otherFees),
    priceCode,
  };
};

const categorizeAccommodation = (
  amount: number,
  priceCode: string,
  buckets: {
    apartments: number;
    dormsFireside: number;
    cottages: number;
    privateCottages: number;
    campingSites: number;
    orphaned: number;
  }
) => {
  if (!amount) return;

  const prefix = priceCode ? priceCode.charAt(0).toUpperCase() : '';
  if (prefix === 'A') buckets.apartments += amount;
  else if (prefix === 'C') buckets.cottages += amount;
  else if (prefix === 'D' || prefix === 'F') buckets.dormsFireside += amount;
  else if (prefix === 'P') buckets.privateCottages += amount;
  else if (prefix === 'T') buckets.campingSites += amount;
  else buckets.orphaned += amount;
};

const TreasurersAnnualReport: React.FC = () => {
  const [data, setData] = useState<FinancialSummaryData>({
    registrationFees: 0,
    accommodationFees: {
      apartments: 0,
      dormsFireside: 0,
      cottages: 0,
      privateCottages: 0,
      campingSites: 0,
      orphaned: 0,
      total: 0,
    },
    fridgeRentals: 0,
    meals: 0,
    creditCardFees: 0,
    otherFees: 0,
    donations: 0,
    totalIncome: 0,
    payments: 0,
    amountStillOwing: 0,
    amountOverPaid: 0,
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const [registrationsSnapshot, settingsSnap] = await Promise.all([
          getDocs(collection(db, 'Registrations')),
          getDoc(doc(db, 'settings', 'camp')),
        ]);
        const settingsData = settingsSnap.exists() ? settingsSnap.data() : null;

        let totalRegistrationFees = 0;
        const accommodationBuckets = {
          apartments: 0,
          dormsFireside: 0,
          cottages: 0,
          privateCottages: 0,
          campingSites: 0,
          orphaned: 0,
        };
        let totalFridgeRentals = 0;
        let totalMeals = 0;
        let totalCreditCardFees = 0;
        let totalOtherFees = 0;
        let totalDonations = 0;
        let totalPayments = 0;
        let amountStillOwing = 0;
        let amountOverPaid = 0;
        let totalIncome = 0;

        registrationsSnapshot.forEach((docSnap) => {
          const registration = { id: docSnap.id, ...(docSnap.data() as any) };

          // Same population rule as Accounts Receivable: Assigned with an invoice
          if (registration.status !== 'Assigned') {
            return;
          }
          const invoiceNumber =
            registration.financial?.invoiceNumber ||
            registration.financialRecord?.invoiceNumber ||
            registration.invoiceNumber ||
            registration.invoice?.invoiceNumber;
          if (!invoiceNumber) {
            return;
          }

          // Authoritative live total (identical to AR / Registration Details)
          const recordTotal = computeRegistrationTotal(registration, settingsData);
          const recordPayments = sumPayments(registration);
          const recordBalance = roundMoney(recordTotal - recordPayments);

          totalIncome = roundMoney(totalIncome + recordTotal);
          totalPayments = roundMoney(totalPayments + recordPayments);

          if (recordBalance > 0) {
            amountStillOwing = roundMoney(amountStillOwing + recordBalance);
          } else if (recordBalance < 0) {
            amountOverPaid = roundMoney(amountOverPaid + Math.abs(recordBalance));
          }

          const breakdown = getLiveChargeBreakdown(registration, settingsData);
          totalRegistrationFees = roundMoney(totalRegistrationFees + breakdown.registrationFees);
          categorizeAccommodation(breakdown.accommodationFee, breakdown.priceCode, accommodationBuckets);
          totalFridgeRentals = roundMoney(totalFridgeRentals + breakdown.fridgeRentals);
          totalMeals = roundMoney(totalMeals + breakdown.meals);
          totalCreditCardFees = roundMoney(totalCreditCardFees + breakdown.creditCardFees);
          totalOtherFees = roundMoney(totalOtherFees + breakdown.otherFees);
          totalDonations = roundMoney(totalDonations + breakdown.donations);
        });

        // Keep income lines reconciled to the authoritative live total.
        // Any tiny rounding/category residual lands in Other Fees.
        const totalAccommodationFees = roundMoney(
          accommodationBuckets.apartments +
            accommodationBuckets.dormsFireside +
            accommodationBuckets.cottages +
            accommodationBuckets.privateCottages +
            accommodationBuckets.campingSites +
            accommodationBuckets.orphaned
        );
        const categorizedIncome = roundMoney(
          totalRegistrationFees +
            totalAccommodationFees +
            totalFridgeRentals +
            totalMeals +
            totalCreditCardFees +
            totalOtherFees +
            totalDonations
        );
        const incomeDelta = roundMoney(totalIncome - categorizedIncome);
        if (incomeDelta !== 0) {
          totalOtherFees = roundMoney(totalOtherFees + incomeDelta);
        }

        // Payments + Amount still Owing - Amount over Paid = Total Income
        const total = roundMoney(totalPayments + amountStillOwing - amountOverPaid);

        setData({
          registrationFees: totalRegistrationFees,
          accommodationFees: {
            apartments: roundMoney(accommodationBuckets.apartments),
            dormsFireside: roundMoney(accommodationBuckets.dormsFireside),
            cottages: roundMoney(accommodationBuckets.cottages),
            privateCottages: roundMoney(accommodationBuckets.privateCottages),
            campingSites: roundMoney(accommodationBuckets.campingSites),
            orphaned: roundMoney(accommodationBuckets.orphaned),
            total: totalAccommodationFees,
          },
          fridgeRentals: totalFridgeRentals,
          meals: totalMeals,
          creditCardFees: totalCreditCardFees,
          otherFees: totalOtherFees,
          donations: totalDonations,
          totalIncome,
          payments: totalPayments,
          amountStillOwing,
          amountOverPaid,
          total,
        });
      } catch (err) {
        setError('Failed to load financial summary data');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const currentDate = new Date().toLocaleDateString();

  const handlePrint = () => {
    window.print();
  };

  const formatMoney = (amount: number) =>
    amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="annual-stupid-report">
      <div style={{ position: 'relative', marginBottom: '8px' }}>
        <h1 style={{ margin: 0, textAlign: 'center' }}>Financial Summary</h1>
        <button
          onClick={handlePrint}
          style={{
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#0056b3')}
          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#007bff')}
        >
          🖨️ Print Report
        </button>
      </div>
      <p style={{ textAlign: 'center', marginBottom: '32px' }}>As of {currentDate}</p>

      {loading ? (
        <div>Loading...</div>
      ) : error ? (
        <div style={{ color: 'red' }}>{error}</div>
      ) : (
        <div style={{ maxWidth: '800px', margin: '0 auto', fontFamily: 'monospace' }}>
          <h2 style={{ borderBottom: '2px solid #333', paddingBottom: '8px' }}>Income</h2>

          <div style={{ marginBottom: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span style={{ paddingLeft: '20px' }}>Registration Fees</span>
              <span>${formatMoney(data.registrationFees)}</span>
            </div>
          </div>

          <div style={{ marginBottom: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span style={{ paddingLeft: '20px' }}>Accommodation Fees:</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}>
              <span style={{ paddingLeft: '40px' }}>Apartments</span>
              <span style={{ marginRight: '200px' }}>${formatMoney(data.accommodationFees.apartments)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}>
              <span style={{ paddingLeft: '40px' }}>Dorms/Fireside</span>
              <span style={{ marginRight: '200px' }}>${formatMoney(data.accommodationFees.dormsFireside)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}>
              <span style={{ paddingLeft: '40px' }}>Cottages</span>
              <span style={{ marginRight: '200px' }}>${formatMoney(data.accommodationFees.cottages)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}>
              <span style={{ paddingLeft: '40px' }}>Private Cottages</span>
              <span style={{ marginRight: '200px' }}>${formatMoney(data.accommodationFees.privateCottages)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}>
              <span style={{ paddingLeft: '40px' }}>Camping Sites</span>
              <span style={{ marginRight: '200px', textDecoration: 'underline' }}>
                ${formatMoney(data.accommodationFees.campingSites)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span style={{ paddingLeft: '20px' }}>Total Accommodation Fees</span>
              <span>${formatMoney(data.accommodationFees.total)}</span>
            </div>
          </div>

          {data.accommodationFees.orphaned > 0 && (
            <div style={{ marginBottom: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                <span style={{ paddingLeft: '20px' }}>Orphaned Accommodations</span>
                <span>${formatMoney(data.accommodationFees.orphaned)}</span>
              </div>
            </div>
          )}

          <div style={{ marginBottom: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span style={{ paddingLeft: '20px' }}>Fridge Rentals</span>
              <span>${formatMoney(data.fridgeRentals)}</span>
            </div>
          </div>

          <div style={{ marginBottom: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span style={{ paddingLeft: '20px' }}>Meals</span>
              <span>${formatMoney(data.meals)}</span>
            </div>
          </div>

          <div style={{ marginBottom: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span style={{ paddingLeft: '20px' }}>Credit Card Fees</span>
              <span>${formatMoney(data.creditCardFees)}</span>
            </div>
          </div>

          <div style={{ marginBottom: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span style={{ paddingLeft: '20px' }}>Other Fees</span>
              <span>${formatMoney(data.otherFees)}</span>
            </div>
          </div>

          <div style={{ marginBottom: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span style={{ paddingLeft: '20px' }}>Donations</span>
              <span>${formatMoney(data.donations)}</span>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span style={{ paddingLeft: '20px', fontWeight: 'bold' }}>Total Income</span>
              <span style={{ fontWeight: 'bold' }}>${formatMoney(data.totalIncome)}</span>
            </div>
          </div>

          <h2 style={{ borderBottom: '2px solid #333', paddingBottom: '8px' }}>Status</h2>

          <div style={{ marginBottom: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span style={{ paddingLeft: '20px' }}>Payments</span>
              <span>${formatMoney(data.payments)}</span>
            </div>
          </div>

          <div style={{ marginBottom: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span style={{ paddingLeft: '20px' }}>Amount still Owing</span>
              <span>${formatMoney(data.amountStillOwing)}</span>
            </div>
          </div>

          <div style={{ marginBottom: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span style={{ paddingLeft: '20px' }}>Amount over Paid</span>
              <span>(${formatMoney(data.amountOverPaid)})</span>
            </div>
          </div>

          <div style={{ marginBottom: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
              <span style={{ paddingLeft: '20px', fontWeight: 'bold' }}>Total</span>
              <span style={{ fontWeight: 'bold' }}>${formatMoney(data.total)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TreasurersAnnualReport;
